// src/FriendsContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { sanitizeShortText, sanitizeSearch } from "./sanitize";

const FriendsContext = createContext();
export const useFriends = () => useContext(FriendsContext);

// Other people's profiles: never pull email. Own-row fetch can still use '*'.
const PUBLIC_PROFILE_COLUMNS = "id, username, display_name, avatar_url, total_xp, current_level";

export const FriendsProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Friends list with their profiles
  const [friends, setFriends] = useState([]);
  
  // Friends' habits data for today
  const [friendsHabits, setFriendsHabits] = useState({});
  
  // Weekly leaderboard (friends + self, ranked by XP this week)
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState(null);

  // Friend requests (received and sent)
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  
  // Current user's profile
  const [userProfile, setUserProfile] = useState(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);

  // Get today's date key
  const getTodayKey = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  };

  // Fetch user's profile
  const fetchUserProfile = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (data) {
        setUserProfile(data);
      } else if (error) {
        if (error.code === 'PGRST116') {
          // Profile doesn't exist, create one
          const newProfile = {
            id: user.id,
            email: user.email,
            username: user.email.split('@')[0].toLowerCase().replace(/\./g, '_') + '_' + user.id.substring(0, 4),
            display_name: user.email.split('@')[0]
          };
          
          const { data: created, error: createError } = await supabase
            .from('user_profiles')
            .insert(newProfile)
            .select()
            .single();
          
          if (created) {
            setUserProfile(created);
          } else if (createError) {
            if (process.env.NODE_ENV === 'development') console.error('Error creating profile:', createError);
          }
        } else if (error.code === '42P01') {
          // Table doesn't exist
          if (process.env.NODE_ENV === 'development') console.error('user_profiles table missing');
        } else {
          if (process.env.NODE_ENV === 'development') console.error('Error fetching profile:', error);
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Profile fetch exception:', err);
    }
  }, [user]);

  // Update user's profile
  const updateUserProfile = async (updates) => {
    if (!user) return { error: { message: 'Not authenticated' } };

    const safe = { ...updates };
    if (safe.username !== undefined) safe.username = sanitizeShortText(safe.username, 50);
    if (safe.display_name !== undefined) safe.display_name = sanitizeShortText(safe.display_name, 50);

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          ...safe,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        if (process.env.NODE_ENV === 'development') console.error('Profile update error:', error);
        return { data: null, error };
      }

      if (data) {
        setUserProfile(data);
      }

      return { data, error: null };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Profile update exception:', err);
      return { data: null, error: { message: 'Failed to update profile' } };
    }
  };

  // Fetch friends list
  const fetchFriends = useCallback(async () => {
    if (!user) return;

    // Get all friendships where user is involved
    const { data: friendships } = await supabase
      .from('friendships')
      .select('user_id, friend_id')
      .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);

    if (!friendships || friendships.length === 0) {
      setFriends([]);
      return;
    }

    // Get friend IDs
    const friendIds = friendships.map(f => 
      f.user_id === user.id ? f.friend_id : f.user_id
    );

    // Fetch friend profiles
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select(PUBLIC_PROFILE_COLUMNS)
      .in('id', friendIds);

    if (profiles) {
      setFriends(profiles);
    }
  }, [user]);

  // Fetch friends' habits and completion status for today (includes current user for compact profile)
  const fetchFriendsHabits = useCallback(async () => {
    if (!user) {
      setFriendsHabits({});
      return;
    }

    const todayKey = getTodayKey();
    const yearMonth = todayKey.slice(0, 7);
    const friendIds = [...friends.map(f => f.id), user.id];
    const habitsData = {};

    // Fetch each friend's habits
    const { data: allHabits } = await supabase
      .from('habits')
      .select('*')
      .in('user_id', friendIds);

    // Fetch today's habit logs for all friends
    const { data: allLogs } = await supabase
      .from('habit_log')
      .select('*')
      .in('user_id', friendIds)
      .eq('date_key', todayKey);

    // Organize data by friend (habits scoped to current month)
    friendIds.forEach(friendId => {
      const friendHabits = allHabits?.filter(
        (h) => h.user_id === friendId && (h.year_month === yearMonth || !h.year_month)
      ) || [];
      const friendLogs = allLogs?.filter(l => l.user_id === friendId) || [];

      habitsData[friendId] = friendHabits.map(habit => {
        const log = friendLogs.find(l => l.habit_name === habit.name);
        return {
          name: habit.name,
          completed: log?.completed || false,
          completedAt: log?.updated_at || null
        };
      });
    });

    setFriendsHabits(habitsData);
  }, [user, friends]);

  // Fetch friend requests
  const fetchFriendRequests = useCallback(async () => {
    if (!user) return;

    try {
      // Received requests
      const { data: received, error: receivedError } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('receiver_id', user.id)
        .eq('status', 'pending');

      if (receivedError && process.env.NODE_ENV === 'development') {
        console.error('Error fetching received requests:', receivedError);
      }

      // Fetch sender profiles for received requests
      if (received && received.length > 0) {
        const senderIds = received.map(r => r.sender_id);
        const { data: senderProfiles } = await supabase
          .from('user_profiles')
          .select(PUBLIC_PROFILE_COLUMNS)
          .in('id', senderIds);

        const receivedWithProfiles = received.map(request => ({
          ...request,
          sender: senderProfiles?.find(p => p.id === request.sender_id)
        }));
        setReceivedRequests(receivedWithProfiles);
      } else {
        setReceivedRequests([]);
      }

      // Sent requests
      const { data: sent, error: sentError } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('sender_id', user.id)
        .eq('status', 'pending');

      if (sentError && process.env.NODE_ENV === 'development') {
        console.error('Error fetching sent requests:', sentError);
      }

      // Fetch receiver profiles for sent requests
      if (sent && sent.length > 0) {
        const receiverIds = sent.map(r => r.receiver_id);
        const { data: receiverProfiles } = await supabase
          .from('user_profiles')
          .select(PUBLIC_PROFILE_COLUMNS)
          .in('id', receiverIds);

        const sentWithProfiles = sent.map(request => ({
          ...request,
          receiver: receiverProfiles?.find(p => p.id === request.receiver_id)
        }));
        setSentRequests(sentWithProfiles);
      } else {
        setSentRequests([]);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching friend requests:', err);
    }
  }, [user]);

  // Search users by username or display name (email excluded from results via RPC)
  const searchUsers = async (query) => {
    if (!user || !query || query.length < 2) return [];

    const searchTerm = sanitizeSearch(query);
    if (searchTerm.length < 2) return [];

    try {
      const { data, error } = await supabase.rpc('search_users', {
        search_term: searchTerm,
        requesting_user_id: user.id
      });

      if (error) {
        return [];
      }

      // Mark existing friends and pending requests
      const friendIds = friends.map(f => f.id);
      const pendingSentIds = sentRequests.map(r => r.receiver_id);
      const pendingReceivedIds = receivedRequests.map(r => r.sender_id);

      return (data || []).map(profile => ({
        ...profile,
        isFriend: friendIds.includes(profile.id),
        hasPendingRequest: pendingSentIds.includes(profile.id) || pendingReceivedIds.includes(profile.id)
      }));
    } catch (err) {
      return [];
    }
  };

  // Send friend request
  const sendFriendRequest = async (receiverId) => {
    if (!user) return { error: { message: 'Not authenticated' } };

    try {
      // Remove any existing request between these two (so re-adding after unfriend works)
      await supabase.from('friend_requests').delete().eq('sender_id', user.id).eq('receiver_id', receiverId);
      await supabase.from('friend_requests').delete().eq('sender_id', receiverId).eq('receiver_id', user.id);

      const { data, error } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: user.id,
          receiver_id: receiverId,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        return { data: null, error };
      }

      // Fetch the receiver profile separately
      if (data) {
        const { data: receiverProfile } = await supabase
          .from('user_profiles')
          .select(PUBLIC_PROFILE_COLUMNS)
          .eq('id', receiverId)
          .single();
        
        const requestWithProfile = { ...data, receiver: receiverProfile };
        setSentRequests(prev => [...prev, requestWithProfile]);
        return { data: requestWithProfile, error: null };
      }

      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: 'Failed to send friend request' } };
    }
  };

  // Accept friend request via secure RPC (SECURITY DEFINER function
  // validates the request belongs to the current user before creating the friendship)
  const acceptFriendRequest = async (requestId, senderId) => {
    if (!user) return { error: 'Not authenticated' };

    const { error } = await supabase.rpc('accept_friend_request', {
      request_id: requestId
    });

    if (!error) {
      setReceivedRequests(prev => prev.filter(r => r.id !== requestId));
      await fetchFriends();
    }

    return { error };
  };

  // Reject friend request
  const rejectFriendRequest = async (requestId) => {
    if (!user) return { error: 'Not authenticated' };

    const { error } = await supabase
      .from('friend_requests')
      .update({ status: 'rejected', updated_at: new Date().toISOString() })
      .eq('id', requestId);

    if (!error) {
      setReceivedRequests(prev => prev.filter(r => r.id !== requestId));
    }

    return { error };
  };

  // Cancel sent friend request
  const cancelFriendRequest = async (requestId) => {
    if (!user) return { error: 'Not authenticated' };

    const { error } = await supabase
      .from('friend_requests')
      .delete()
      .eq('id', requestId);

    if (!error) {
      setSentRequests(prev => prev.filter(r => r.id !== requestId));
    }

    return { error };
  };

  // Remove friend
  const removeFriend = async (friendId) => {
    if (!user) return { error: 'Not authenticated' };

    const [userId, fId] = friendId < user.id 
      ? [friendId, user.id] 
      : [user.id, friendId];

    const { error } = await supabase
      .from('friendships')
      .delete()
      .eq('user_id', userId)
      .eq('friend_id', fId);

    if (!error) {
      // Delete any friend_request between these two so they can re-add each other
      await supabase.from('friend_requests').delete().eq('sender_id', user.id).eq('receiver_id', friendId);
      await supabase.from('friend_requests').delete().eq('sender_id', friendId).eq('receiver_id', user.id);

      setFriends(prev => prev.filter(f => f.id !== friendId));
      setFriendsHabits(prev => {
        const updated = { ...prev };
        delete updated[friendId];
        return updated;
      });
      setSentRequests(prev => prev.filter(r => r.receiver_id !== friendId));
      setReceivedRequests(prev => prev.filter(r => r.sender_id !== friendId));
    }

    return { error };
  };

  // Fetch weekly leaderboard
  const fetchWeeklyLeaderboard = useCallback(async () => {
    if (!user) {
      setWeeklyLeaderboard(null);
      return;
    }
    try {
      const { data, error } = await supabase.rpc('get_friends_weekly_leaderboard');
      if (error) {
        if (process.env.NODE_ENV === 'development') console.error('Leaderboard fetch error:', error);
        setWeeklyLeaderboard(null);
        return;
      }
      setWeeklyLeaderboard(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Leaderboard fetch exception:', err);
      setWeeklyLeaderboard(null);
    }
  }, [user]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    if (!user) {
      setFriends([]);
      setFriendsHabits({});
      setWeeklyLeaderboard(null);
      setReceivedRequests([]);
      setSentRequests([]);
      setUserProfile(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      await fetchUserProfile();
      await fetchFriends();
      await fetchFriendRequests();
      await fetchWeeklyLeaderboard();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching friends data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchUserProfile, fetchFriends, fetchFriendRequests, fetchWeeklyLeaderboard]);

  // Initial data load
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Fetch friends' habits when friends list changes (includes current user's habits)
  useEffect(() => {
    if (user) {
      fetchFriendsHabits();
    }
  }, [user, friends, fetchFriendsHabits]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user) return;

    // Subscribe to friend requests changes
    const requestsSubscription = supabase
      .channel('friend_requests_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friend_requests',
          filter: `receiver_id=eq.${user.id}`
        },
        () => {
          fetchFriendRequests();
        }
      )
      .subscribe();

    // Subscribe to friendships changes
    const friendshipsSubscription = supabase
      .channel('friendships_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friendships'
        },
        () => {
          fetchFriends();
        }
      )
      .subscribe();

    // Subscribe to habit_log changes for friends
    const habitLogSubscription = supabase
      .channel('habit_log_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'habit_log'
        },
        (payload) => {
          // Only refresh if it's from a friend or self (affects leaderboard)
          const friendIds = friends.map(f => f.id);
          if (friendIds.includes(payload.new?.user_id) || friendIds.includes(payload.old?.user_id) || payload.new?.user_id === user?.id || payload.old?.user_id === user?.id) {
            fetchFriendsHabits();
            fetchWeeklyLeaderboard();
          }
        }
      )
      .subscribe();

    return () => {
      requestsSubscription.unsubscribe();
      friendshipsSubscription.unsubscribe();
      habitLogSubscription.unsubscribe();
    };
  }, [user, friends, fetchFriendRequests, fetchFriends, fetchFriendsHabits, fetchWeeklyLeaderboard]);

  return (
    <FriendsContext.Provider
      value={{
        friends,
        friendsHabits,
        weeklyLeaderboard,
        receivedRequests,
        sentRequests,
        userProfile,
        isLoading,
        searchUsers,
        sendFriendRequest,
        acceptFriendRequest,
        rejectFriendRequest,
        cancelFriendRequest,
        removeFriend,
        updateUserProfile,
        refreshData: fetchAllData,
        refreshFriendsHabits: fetchFriendsHabits,
        refreshWeeklyLeaderboard: fetchWeeklyLeaderboard
      }}
    >
      {children}
    </FriendsContext.Provider>
  );
};
